"use client";

/**
 * Shared chat scroll helpers.
 */

import { useCallback, useRef } from "react";

export const INITIAL_CHAT_SCROLL_DELAY_MS = 300;

/** Gap kept between the pane top and the streaming message top when anchored. */
export const STREAM_ANCHOR_PADDING_PX = 8;

/** Within this distance of the bottom, a user scroll counts as "at the bottom" (opts back into follow). */
export const AT_BOTTOM_EPSILON_PX = 24;

/** Scroll events within this distance of our last programmatic write are treated as our own, not the user's. */
const PROGRAMMATIC_SCROLL_TOLERANCE_PX = 2;

/** DOM attribute marking the currently-streaming message element (the scroll anchor). */
export const STREAM_ANCHOR_ATTR = "data-stream-anchor";

export function shouldScheduleVisibleInitialChatScroll(args: {
  collapsed: boolean;
  forceHidden: boolean | undefined;
  messagesLength: number;
  hasFired: boolean;
}): boolean {
  return !args.collapsed && !args.forceHidden && args.messagesLength > 0 && !args.hasFired;
}

export function getChatBottomScrollTop(el: Pick<HTMLElement, "scrollHeight" | "clientHeight">): number {
  return Math.max(el.scrollHeight - el.clientHeight, 0);
}

/**
 * ScrollTop target while a message streams: follow the bottom, but never let
 * the top of the streaming message (at `anchorOffsetTop` within the scroll
 * content) rise above the top of the pane.
 */
export function getStreamAnchorScrollTop(
  el: Pick<HTMLElement, "scrollHeight" | "clientHeight">,
  anchorOffsetTop: number,
  paddingPx: number = STREAM_ANCHOR_PADDING_PX,
): number {
  const anchorTop = Math.max(anchorOffsetTop - paddingPx, 0);
  return Math.min(getChatBottomScrollTop(el), anchorTop);
}

function scrollChatToBottom(
  el: Pick<HTMLElement, "scrollHeight" | "clientHeight" | "scrollTop"> | null,
): void {
  if (!el) return;
  el.scrollTop = getChatBottomScrollTop(el);
}

export function scheduleInitialChatScrollToBottom(
  el: Pick<HTMLElement, "scrollHeight" | "clientHeight" | "scrollTop"> | null,
  delayMs = INITIAL_CHAT_SCROLL_DELAY_MS,
): () => void {
  if (!el) return () => {};

  const timeoutId = window.setTimeout(() => {
    scrollChatToBottom(el);
  }, delayMs);

  return () => {
    window.clearTimeout(timeoutId);
  };
}

/** Minimal rect surface so tests can pass plain objects; HTMLElement satisfies it. */
export type StreamAnchorElement = { getBoundingClientRect(): { top: number } };

export type StreamScrollContainer = Pick<
  HTMLElement,
  "scrollHeight" | "clientHeight" | "scrollTop"
> &
  StreamAnchorElement;

/** Offset of the anchor's top within the container's scroll content. */
function getStreamAnchorOffsetTop(
  el: StreamScrollContainer,
  anchor: StreamAnchorElement,
): number {
  return (
    anchor.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop
  );
}

export type ChatStreamScrollController = {
  /** Attach to the scroll container's onScroll (pass event.currentTarget). */
  handleScrollEvent(el: StreamScrollContainer): void;
  /**
   * Apply the streaming auto-scroll policy for the current render tick:
   * follow the bottom until the top of the streaming message (anchor) would
   * leave the pane, then hold there. No-op once the user has scrolled away.
   * Returns a cleanup that cancels the settle passes.
   */
  followStreamTick(
    el: StreamScrollContainer,
    anchor: StreamAnchorElement | null,
  ): () => void;
  /**
   * Bottom-scroll for non-streaming updates (new discrete messages, layout
   * settles). Suppressed while the user has scrolled away or the anchor clamp
   * engaged this turn — so a finished long response stays readable from the top.
   */
  scrollForIdleUpdate(el: StreamScrollContainer): () => void;
  /** New turn or explicit layout event (send, panel expand, task activation): re-enable auto-follow and scroll to the bottom. */
  resetAndScrollToBottom(el: StreamScrollContainer): () => void;
  /**
   * Reveal bottom content (e.g. a CTA appearing mid-turn) WITHOUT discarding
   * a manual override: no-op when the user has scrolled away; otherwise
   * releases the anchor clamp and scrolls to the bottom.
   */
  scrollToBottomUnlessOverridden(el: StreamScrollContainer): () => void;
};

/**
 * Per-component controller instance + onScroll handler for a chat scroll
 * container. Shared by the LevAI panel and the onboarding chat; the caller
 * owns the effect that decides which controller method fires per tick.
 */
export function useChatStreamScroll(): {
  controller: ChatStreamScrollController;
  onScroll: (e: React.UIEvent<HTMLDivElement>) => void;
} {
  const controllerRef = useRef<ChatStreamScrollController | null>(null);
  if (controllerRef.current === null) {
    controllerRef.current = createChatStreamScrollController();
  }
  const onScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    controllerRef.current?.handleScrollEvent(e.currentTarget);
  }, []);
  return { controller: controllerRef.current, onScroll };
}

/**
 * Stateful scroll policy for a streaming chat pane (LEV-1296).
 *
 * Invariants, in priority order:
 * 1. The user's own scroll position always wins — any manual scroll suspends
 *    auto-scroll until the next turn (or until they return to the bottom).
 * 2. While a message streams, auto-scroll never pushes the top of that
 *    message above the pane top (the anchor clamp).
 * 3. Everything else (sends, layout events, discrete new messages) keeps the
 *    classic scroll-to-bottom behavior.
 */
export function createChatStreamScrollController(): ChatStreamScrollController {
  /** User scrolled away from the bottom — all auto-scroll suspended. */
  let overridden = false;
  /** The anchor clamp engaged this turn — idle bottom-scrolls stay suppressed so the reading position survives stream completion. */
  let anchorEngaged = false;
  /** User deliberately returned to the bottom mid-stream — follow the bottom for the rest of the turn. */
  let followBottom = false;
  let lastProgrammaticTop: number | null = null;

  const write = (el: StreamScrollContainer, top: number): void => {
    el.scrollTop = top;
    lastProgrammaticTop = el.scrollTop;
  };

  /**
   * Apply now and re-apply after layout settles (paint + next frame + 80ms),
   * mirroring the multi-pass cadence the old scheduleChatScrollToBottom used
   * for late-settling message heights. `computeTarget` returning null skips
   * the write. Every pass re-checks the live override flag: a user scroll
   * landing between passes must win instantly — a queued pass may never
   * yank the pane back (PR #537 review, P1).
   */
  const schedulePasses = (
    el: StreamScrollContainer,
    computeTarget: () => number | null,
  ): (() => void) => {
    const apply = () => {
      if (overridden) return;
      const target = computeTarget();
      if (target !== null) write(el, target);
    };
    apply();

    let nestedRafId: number | null = null;
    const rafId = requestAnimationFrame(() => {
      apply();
      nestedRafId = requestAnimationFrame(apply);
    });
    const timeoutId = window.setTimeout(apply, 80);

    return () => {
      cancelAnimationFrame(rafId);
      if (nestedRafId !== null) cancelAnimationFrame(nestedRafId);
      window.clearTimeout(timeoutId);
    };
  };

  const noop = () => {};

  return {
    handleScrollEvent(el) {
      if (
        lastProgrammaticTop !== null &&
        Math.abs(el.scrollTop - lastProgrammaticTop) <= PROGRAMMATIC_SCROLL_TOLERANCE_PX
      ) {
        return;
      }
      const distanceFromBottom = getChatBottomScrollTop(el) - el.scrollTop;
      if (distanceFromBottom <= AT_BOTTOM_EPSILON_PX) {
        overridden = false;
        followBottom = true;
        return;
      }
      overridden = true;
    },

    followStreamTick(el, anchor) {
      if (overridden) return noop;
      return schedulePasses(el, () => {
        const bottom = getChatBottomScrollTop(el);
        if (followBottom || !anchor) return bottom;
        const target = getStreamAnchorScrollTop(el, getStreamAnchorOffsetTop(el, anchor));
        if (target < bottom) anchorEngaged = true;
        // Only follow downward — never yank the pane up mid-stream.
        return target > el.scrollTop ? target : null;
      });
    },

    scrollForIdleUpdate(el) {
      if (overridden || anchorEngaged) return noop;
      return schedulePasses(el, () => getChatBottomScrollTop(el));
    },

    resetAndScrollToBottom(el) {
      overridden = false;
      anchorEngaged = false;
      followBottom = false;
      return schedulePasses(el, () => getChatBottomScrollTop(el));
    },

    scrollToBottomUnlessOverridden(el) {
      if (overridden) return noop;
      anchorEngaged = false;
      return schedulePasses(el, () => getChatBottomScrollTop(el));
    },
  };
}
